import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Share2, Download, Check, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import CaptionBox from "@/components/share/CaptionBox";

export const scoreColor = (s) => (s >= 4 ? "#10B981" : s >= 3 ? "#F59E0B" : "#EF4444");

async function renderPng(node) {
  return toPng(node, { pixelRatio: 2, cacheBust: true, backgroundColor: "#1E293B", skipFonts: false });
}

const dataUrlToFile = async (dataUrl, name) => new File([await (await fetch(dataUrl)).blob()], name, { type: "image/png" });

export default function ShareCardFrame({ children, fileName, shareText, shareUrl, captions, testid = "share-card" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState("linkedin");
  const caption = captions?.[tab] || shareText;

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(caption); return true; } catch { return false; }
  };

  const share = async () => {
    setBusy(true);
    try {
      const png = await renderPng(ref.current);
      const file = await dataUrlToFile(png, `${fileName}.png`);
      const copiedCaption = await copyCaption();
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: caption, title: "my360boss" });
      } else {
        const a = document.createElement("a");
        a.href = png; a.download = `${fileName}.png`; a.click();
        toast.success(copiedCaption ? "Card downloaded + caption copied — paste both into your post" : "Card downloaded — copy the caption below");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not generate the image");
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const png = await renderPng(ref.current);
      const a = document.createElement("a");
      a.href = png; a.download = `${fileName}.png`; a.click();
      toast.success("PNG downloaded");
    } catch {
      toast.error("Could not generate the image");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    const url = shareUrl || window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      window.prompt("Copy this link:", url);
    }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid={`${testid}-wrapper`}>
      <div className="w-full max-w-[420px]">
        <div ref={ref} data-testid={testid} className="w-full aspect-[4/5] rounded-2xl overflow-hidden text-white flex flex-col" style={{ backgroundColor: "#1E293B", border: "2px solid #F8FAFC", fontFamily: "'Space Grotesk', 'Manrope', sans-serif" }}>
          {children}
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-2 w-full max-w-[420px]">
        <Button data-testid={`${testid}-share-btn`} onClick={share} disabled={busy} className="flex-1 h-11 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full gap-2 font-semibold">
          <Share2 size={16} /> {busy ? "Rendering..." : "Share to Social"}
        </Button>
        <Button data-testid={`${testid}-download-btn`} onClick={download} disabled={busy} variant="outline" className="h-11 rounded-full gap-2"><Download size={16} /> PNG</Button>
        <Button data-testid={`${testid}-copy-link-btn`} onClick={copyLink} variant="outline" className="h-11 rounded-full gap-2">{copied ? <Check size={16} /> : <Link2 size={16} />}</Button>
      </div>
      <CaptionBox captions={captions} testid={`${testid}-captions`} tab={tab} onTabChange={setTab} />
    </div>
  );
}

export const CardFooter = ({ text }) => (
  <div className="mt-auto px-6 pb-5">
    <div className="h-px w-full mb-3" style={{ backgroundColor: "rgba(248,250,252,0.25)" }} />
    <p className="text-[10px] leading-snug" style={{ color: "#94A3B8" }}>{text}</p>
  </div>
);

export const Wordmark = () => (
  <span className="font-mono text-[11px] tracking-widest uppercase" style={{ color: "#94A3B8" }}>
    my<span style={{ color: "#F8FAFC" }}>360</span><span style={{ color: "#10B981" }}>boss</span>
  </span>
);
